import { isValidEmail, isValidPersonName, isValidPhoneNumber, normalizePersonName } from '@workspace/utils';
import { Description, Field, Input, InputProps, Label, LabelProps, PhoneFormatHint, TextError } from '../form';
import { RenderedContent } from '../../utils';
import { ErrorMessage } from '@hookform/error-message';
import { FieldValues, Path, RegisterOptions, get, useFormContext } from 'react-hook-form';

type NormalizeType = 'uppercase' | 'uppercaseStrict';

export interface FieldInputProps<TFormValues extends Record<string, never> = any>
  extends InputProps, LabelProps {
  name: Path<TFormValues> | string;
  validation?: RegisterOptions<FieldValues, string>;
  description?: RenderedContent;
  /**
   * Normalise automatiquement la valeur au blur :
   * - 'uppercase' : convertit en majuscules
   * - 'uppercaseStrict' : majuscules + suppression accents et caractères spéciaux
   */
  normalize?: NormalizeType;
}


const VALIDATORS = {
  tel: { isValidPhoneNumber },
  email: { isValidEmail },
} as const;

/**
 * Input connecté au formulaire avec validation automatique
 * - Validation requise automatique
 * - Validation téléphone (10 chiffres) pour type="tel"
 * - Validation email pour type="email"
 * - Désactivation auto pendant soumission
 * - Gestion d'erreurs intégrée
 * - Option de normalisation (majuscules, nettoyage caractères spéciaux)
 */
export function FieldInput({
  type,
  label,
  name,
  required = true,
  children,
  validation = {},
  disabled,
  description,
  className,
  normalize,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  style: _style,
  onBlur,
  ...props
}: FieldInputProps) {
  const methods = useFormContext();

  // Ajouter les validateurs spécifiques au type
  if (type && type in VALIDATORS) {
    validation.validate = {
      ...validation.validate,
      ...VALIDATORS[type as keyof typeof VALIDATORS],
    };
  }

  // Interdire les caractères spéciaux sur les noms / prénoms
  if (normalize === 'uppercaseStrict') {
    validation.validate = {
      ...validation.validate,
      isValidPersonName,
    };
  }

  // Handler pour la normalisation au blur
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (normalize) {
      const value = e.target.value;
      let normalized: string;

      if (normalize === 'uppercaseStrict') {
        // Caractères interdits : on ne les supprime pas silencieusement, on affiche l'erreur
        if (isValidPersonName(value)) {
          methods.trigger(name);
          onBlur?.(e);
          return;
        }
        normalized = normalizePersonName(value);
      } else {
        normalized = value.toUpperCase().trim();
      }

      if (normalized !== value) {
        methods.setValue(name, normalized, { shouldValidate: true });
      }
    }
    // Appeler le onBlur original si fourni
    onBlur?.(e);
  };


  return (
    <Field className={className}>
      <Label required={required}>{label}</Label>
      <Input
        type={type}
        {...methods.register(name, {
          required: {
            value: required,
            message: 'Ce champs est requis',
          },
          ...validation,
          ...(type === 'email' && {
            setValueAs: (v: string) => v?.toLowerCase?.() ?? v,
          }),
        })}
        disabled={methods.formState.isSubmitting || disabled}
        error={get(methods.formState.errors, name)}
        onBlur={handleBlur}
        {...props}
      />
      {(type === 'tel' || props.autoComplete === 'tel') && !disabled && <PhoneFormatHint />}
      <ErrorMessage
        errors={methods.formState.errors}
        name={name}
        render={({ message }) => <TextError>{message}</TextError>}
      />
      <Description>{description}</Description>
      {children}
    </Field>
  );
}
